// "How Cerebro works": the short explanation of PARA and GTD that the web
// and Android apps both show, from Settings and once at first sign-in. One
// text for both, so the two apps never explain Cerebro differently.

export interface GuideSection {
  title: string;
  bullets: readonly string[];
}

export const GUIDE_TITLE = 'How Cerebro works';

export const HOW_CEREBRO_WORKS: readonly GuideSection[] = [
  {
    title: 'PARA: where things live',
    bullets: [
      'Projects have a finish line: "Plan the offsite", "Renew passports". Completing a project moves it to the Archive.',
      'Areas are ongoing responsibilities with no end date: Home, Health, Work.',
      'Resources are reference you may want later: recipes, articles, ideas.',
      'Archive holds anything finished or inactive. Archived items leave Today and the lists; unarchive one to bring it back.',
    ],
  },
  {
    title: 'How to use it',
    bullets: [
      'Capture everything: type or speak a thought the moment you have it.',
      'Cerebro files each capture as a task or a note. Tasks live in a Project or an Area; notes can also live in a Resource.',
      'When Cerebro is unsure (below your confidence threshold in Settings), the capture waits in the Inbox with a suggestion. Confirm it or choose another place.',
      'If something lands in the wrong place, open it and move it.',
    ],
  },
  {
    title: 'Why it works',
    bullets: [
      'Things are sorted by what you will do with them, not by topic, so every list stays short and useful.',
      'Your head is for having ideas, not holding them. Once a thought is captured, you can let it go.',
    ],
  },
  {
    title: 'The mindset',
    bullets: [
      'Capture first, sort later. Never stop to decide where something belongs.',
      'Good enough filing beats perfect filing; the Inbox catches the rest.',
    ],
  },
  {
    title: 'GTD: getting things done',
    bullets: [
      'Capture: get it out of your head and into Cerebro.',
      'Clarify: decide what it is, a task you can act on or a note to keep. Cerebro makes the first guess.',
      'Organize: every task has a status. Next action is the very next thing you can do; Waiting for means someone else has the ball; Someday means not now, maybe later.',
      'Contexts say where a next action can be done: @Phone, @Computer, @Errands. Change the list in Settings under Contexts.',
      'Review: look over your lists often enough to trust them.',
      'Do: pick from Today and get to work.',
    ],
  },
  {
    title: 'Every day: Today',
    bullets: [
      'Focus: the tasks you chose for today (Focus today on the website, the star on the phone).',
      'Inbox: captures waiting for you to confirm.',
      'Due: anything overdue or due today.',
      'Next actions, grouped by context, then Waiting for.',
      'Your daily digest arrives at the time you set in Settings.',
    ],
  },
  {
    title: 'Every week: the review',
    bullets: [
      'Empty the Inbox.',
      'Read through All tasks. Move Someday tasks to Next action when their time comes.',
      'Give every active project a next action; the Projects list flags those without one.',
      'Complete or archive what is finished.',
    ],
  },
];

/** Words in the whole guide, titles included. */
export function guideWordCount(sections: readonly GuideSection[] = HOW_CEREBRO_WORKS): number {
  const text = [GUIDE_TITLE, ...sections.flatMap(section => [section.title, ...section.bullets])].join(' ');
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Where a device remembers that this user has seen the guide, so it opens by
 * itself only once per user on each device.
 */
export function guideSeenKey(userId: string): string {
  return `guide_seen:${userId}`;
}
